<template>
  <b-modal
    :id="modalId"
    centered
    :title="data ? 'Update Project Activity' : 'Add Project Activity'"
    @hide="$emit('close')"
    hide-header-close
    @show="resetModal"
    @hidden="onHidden"
    @ok.prevent="handleOk"
    size="md"
  >
    <b-form ref="form" @submit.stop.prevent="handleSubmit">
      <b-form-group label="Task" :state="getState('task')" invalid-feedback="Task is required.">
        <b-form-input v-model="form.task" placeholder="Task" :state="getState('task')" maxlength="255" />
      </b-form-group>
      <b-row>
        <b-col class="col-6">
          <b-form-group label="Start Date" :state="getState('start_date')" invalid-feedback="Start Date is required.">
            <v-date-picker
              v-model="form.start_date"
              type="date"
              format="DD-MM-YYYY"
              placeholder="Select Start Date"
              :disabled-date="disableDateBefore"
              @input="clearError('start_date')"
            />
          </b-form-group>
        </b-col>
        <b-col class="col-6">
          <b-form-group label="End Date" :state="getState('end_date')" invalid-feedback="End Date is required.">
            <v-date-picker
              v-model="form.end_date"
              type="date"
              format="DD-MM-YYYY"
              placeholder="Select End Date"
              :disabled-date="disableDateBefore"
              @input="clearError('end_date')"
            />
          </b-form-group>
        </b-col>
      </b-row>
      <b-form-group label="Percentage" :state="getState('percentage')" invalid-feedback="Percentage is required.">
        <div class="d-flex align-items-center gap-2">
          <b-form-input
            v-model.number="form.percentage"
            :state="getState('percentage')"
            type="range"
            min="0"
            max="100"
            step="1"
            class="flex-grow-1"
          />
          <div class="progress-value ms-2">
            {{ form.percentage === null || form.percentage === undefined ? 0 : form.percentage }}%
          </div>
        </div>
      </b-form-group>
      <b-form-group label="Assignee" :state="getState('nik')" invalid-feedback="Assignee is required.">
        <v-select
          v-model="form.nik"
          multiple
          :options="memberOptions"
          label="name"
          :reduce="(e) => e.nik"
          placeholder="Select Assignee"
        />
      </b-form-group>
    </b-form>

    <template #modal-footer>
      <b-button variant="primary" @click="handleOk" :disabled="isLoading" :key="isLoading">
        <b-spinner v-if="isLoading" small type="grow" class="me-2"></b-spinner>
        <span>{{ isLoading ? "Submitting..." : "Submit" }}</span>
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import { formatTimeToHHMM } from "../utils/utils";
import moment from "moment";

export default {
  name: "ProjectActivityModal",

  props: {
    modalId: { type: String, default: "project-activity-modal" },
    data: { type: Object, default: null },
    isLoading: { type: Boolean, default: false },
    id: { type: Number, default: null },
    memberOptions: { type: Array, default: () => [] },
  },

  data() {
    return {
      form: this.getInitialForm(),
      formErrors: {},
      suppressValidation: false,
      selectedFile: null,
      previewUrl: null,
      isPdf: false,
      isDragging: false,
      submitted: false,
    };
  },

  watch: {
    data: {
      deep: true,
      handler(val) {
        this.populateFromData(val);
      },
    },
  },

  computed: {},

  methods: {
    formatTimeToHHMM,

    disableDateBefore(date) {
      const today = moment().startOf("day");
      const current = moment(date);
      return current.isBefore(today, "day");
    },

    clearError(field) {
      if (this.formErrors[field]) {
        this.$delete(this.formErrors, field);
      }
    },

    getState(field) {
      if (this.formErrors[field]) return false;
      return null;
    },

    onHidden() {
      this.resetModal();
      this.$emit("close");
    },

    handleOk(bvModalEvt) {
      bvModalEvt.preventDefault();
      this.handleSubmit();
    },

    validateAll() {
      this.formErrors = {};

      if (!this.form.start_date) this.formErrors.start_date = "Start Date is required.";
      if (!this.form.end_date) this.formErrors.end_date = "End Date is required.";
      if (!this.form.task) this.formErrors.task = "Task is required.";
      if (!this.form.percentage) {
        this.formErrors.percentage = "Percentage is required.";
      } else if (Number(this.form.percentage) > 100) {
        this.formErrors.percentage = "Percentage cannot exceed 100.";
      } else if (Number(this.form.percentage) < 0) {
        this.formErrors.percentage = "Percentage cannot be negative.";
      }

      return Object.keys(this.formErrors).length === 0;
    },

    formatDateOnly(dateStr) {
      if (!dateStr) return null;
      const date = new Date(dateStr);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    },

    handleSubmit() {
      this.submitted = true;
      if (!this.validateAll()) return;

      const payload = { ...this.form };

      payload.id = this?.id || null;
      if (this.form.start_date) payload.start_date = this.formatDateOnly(this.form.start_date);
      if (this.form.end_date) payload.end_date = this.formatDateOnly(this.form.end_date);

      const submittedPayload = { data: { ...payload } };

      if (this.data?.id) {
        submittedPayload.id = this.data?.id;
        delete submittedPayload.data.id;
      }

      this.$emit("submit-activity", submittedPayload);
    },

    getInitialForm() {
      return {
        id: null,
        task: null,
        start_date: null,
        end_date: null,
        percentage: 0,
        nik: [],
        id: this.data?.id,
      };
    },

    populateFromData(d) {
      this.form = {
        task: d?.task,
        start_date: new Date(d?.start_date),
        end_date: new Date(d?.end_date),
        percentage: d?.percentage,
        nik: JSON.parse(d?.nik || "[]"),
        id: d?.id,
      };

      this.formErrors = {};
    },

    async resetModal() {
      if (this.isLoading) return;

      this.suppressValidation = true;

      await this.$nextTick();

      if (this.data) {
        this.populateFromData(this.data);
      } else {
        this.form = this.getInitialForm();
        this.formErrors = {};
      }

      this.$nextTick(() => {
        this.suppressValidation = false;
      });
    },
  },

  beforeDestroy() {},
};
</script>

<style scoped>
:deep(.table-header) {
  font-size: 11px !important;
}

:deep(.vgt-table td, .vgt-table th) {
  font-size: 11px !important;
}

:deep(.vgt-left-align) {
  align-content: center !important;
}

.dropzone {
  background-color: #f8f9fa;
  border: 2px dashed #dee2e6 !important;
  cursor: pointer;
  transition: all 0.3s ease;
}

.dropzone:hover,
.dropzone.border-danger {
  border-color: #e63757 !important;
  background-color: #fff5f7;
}

.dropzone.is-invalid {
  border-color: #dc3545 !important;
}

.course-icon {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background-color: #fce4e4;
  display: flex;
  align-items: center;
  justify-content: center;
}

.gap-2 {
  gap: 0.5rem;
}

.me-1 {
  margin-right: 0.25rem;
}

.me-2 {
  margin-right: 0.5rem;
}

.me-3 {
  margin-right: 1rem;
}

/* PDF Preview Styles */
.pdf-preview {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
}

.pdf-preview .pdf-icon-container {
  width: 80px;
  height: 100px;
  background: linear-gradient(135deg, #e63757 0%, #c82333 100%);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(230, 55, 87, 0.3);
}

.pdf-preview .pdf-icon-container i {
  font-size: 2.5rem;
  color: #fff;
}

.pdf-preview .pdf-label {
  margin-top: 1rem;
  font-weight: 600;
  color: #1a202c;
}
</style>
